import { useState, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import LoadingButton from '../../Components/LoadingButton';
import Modal from '../../Components/Modal';
import Table from '../../Components/Table';
import FormAutocomplete from '../../Components/forms/FormAutocomplete';
import FormSwitch from '../../Components/forms/FormSwitch';
import FormTextbox from '../../Components/forms/FormTextbox';

import { ContentContainer, ContentBox, TableBox } from '../../theme';
import {
  getConcepts,
  getConceptGroups,
  createConcept,
  editConcept,
  getDepartments,
} from '../../api/services';
import { useModal } from '../../hooks';
import { CONCEPT_COLUMNS } from '../../constants/tables/columns';
import { CONCEPT_INITIAL_VALUES } from '../../constants/forms/initialValues';
import {
  CONCEPT_EDIT_SCHEMA,
  CONCEPT_ADD_SCHEMA,
} from '../../constants/forms/schemas';
import useAuth from '../../context/useAuth';
import Autocomplete from '../../Components/Autocomplete';

const ConceptGroups = () => {
  const [concepts, setConcepts] = useState(null);
  const [department, setDepartment] = useState(null);
  const [editData, setEditData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departmentQuery] = useState({ isActive: true });
  const [query, setQuery] = useState(null);

  const { setUser } = useAuth();

  const [open, setOpen] = useModal({ ref: false });

  const fetchData = useCallback(async () => {
    try {
      const res = await getConcepts();
      setConcepts(res);
      setLoading(false);
    } catch (error) {
      if (error.status === 401) setUser(null);
      toast.error(error.message);
    }
  }, [setUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditRequest = async data => {
    setEditData(data.row);
    const [editDepartment] = await getDepartments({
      _id: data.row.conceptGroup.department,
    });
    setDepartment(editDepartment);
    setQuery({ isActive: true, department: editDepartment._id });
    setOpen(true);
  };

  const handleOpen = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setDepartment(null);
    setQuery(null);
  };

  const handleDepartmentChange = (newDepartment, setFieldValue) => {
    setDepartment(newDepartment);
    setFieldValue('conceptGroup', null);
    setQuery({ isActive: true, department: newDepartment._id });
  };

  return (
    <>
      <ContentContainer maxWidth="xl">
        <ContentBox>
          <TableBox>
            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                disabled={loading}
                endIcon={<AddIcon />}
                onClick={handleOpen}
                variant="contained"
              >
                Nuevo
              </Button>
            </Box>

            <Box sx={{ height: '100%' }}>
              <Table
                columns={CONCEPT_COLUMNS(handleEditRequest)}
                loading={loading}
                rows={concepts}
              />
            </Box>
          </TableBox>
        </ContentBox>
      </ContentContainer>

      {/* New/edit Modal */}
      <Modal
        disabled={loading}
        onClose={handleClose}
        open={open}
        title={`${editData ? 'Editar' : 'Nuevo'} concepto`}
        width="sm"
      >
        <Formik
          initialValues={editData || CONCEPT_INITIAL_VALUES}
          onSubmit={async values => {
            try {
              setLoading(true);

              const data = { ...values, conceptGroup: values.conceptGroup._id };

              editData ? await editConcept(data) : await createConcept(data);
              toast.success(
                `Concepto ${editData ? 'editado' : 'creado'} con éxito.`
              );

              await fetchData();
              handleClose();
            } catch (error) {
              setLoading(false);

              if (error.status === 409) {
                setError(error.message);
                return;
              }

              if (error.status === 401) setUser(null);
              toast.error(error.message);
            }
          }}
          validationSchema={editData ? CONCEPT_EDIT_SCHEMA : CONCEPT_ADD_SCHEMA}
        >
          {({ setFieldValue }) => (
            <Form>
              <Grid container spacing={2} sx={{ p: 2, mb: 4 }}>
                {error && (
                  <Grid item xs={12}>
                    <Alert
                      onClose={() => setError(null)}
                      severity="error"
                      sx={{
                        mb: 2,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'error.dark',
                      }}
                    >
                      {error}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormTextbox
                    disabled={loading}
                    label="Concepto"
                    name="conceptName"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    disabled={loading}
                    label="Departamento"
                    onChange={newDepartment =>
                      handleDepartmentChange(newDepartment, setFieldValue)
                    }
                    property="departmentName"
                    query={departmentQuery}
                    source={getDepartments}
                    value={department}
                  />
                </Grid>

                {query && (
                  <Grid item xs={12}>
                    <FormAutocomplete
                      disabled={loading}
                      label="Grupo"
                      name="conceptGroup"
                      property="conceptGroupName"
                      query={query}
                      source={getConceptGroups}
                    />
                  </Grid>
                )}

                {editData && (
                  <Grid item xs={12}>
                    <FormSwitch
                      disabled={loading}
                      name="isActive"
                      offLabel="Inactivo"
                      onLabel="Activo"
                    />
                  </Grid>
                )}
              </Grid>

              {/* Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  px: 2,
                }}
              >
                <Button color="error" disabled={loading} onClick={handleClose}>
                  Cancelar
                </Button>
                <LoadingButton
                  icon={editData ? <EditIcon /> : <AddIcon />}
                  loading={loading}
                  text={editData ? 'Editar' : 'Guardar'}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default ConceptGroups;
